import { useState, useEffect, useCallback } from 'react';
import { Heart } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { tracksApi } from '../api/tracks';

interface LikeButtonProps {
  trackId: string;
  size?: number;
  className?: string;
  showOnlyWhenAuthenticated?: boolean;
}

export default function LikeButton({
  trackId,
  size = 18,
  className = '',
  showOnlyWhenAuthenticated = true,
}: LikeButtonProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const queryClient = useQueryClient();

  // Check if track is liked on mount
  useEffect(() => {
    if (!isAuthenticated || !trackId) return;

    let mounted = true;
    tracksApi.checkLiked([trackId]).then((result) => {
      if (mounted) {
        setIsLiked(result[trackId] || false);
      }
    }).catch(() => {
      // Ignore errors
    });

    return () => { mounted = false; };
  }, [trackId, isAuthenticated]);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated || isLoading) return;

    setIsLoading(true);
    const wasLiked = isLiked;

    // Optimistic update
    setIsLiked(!wasLiked);

    try {
      if (wasLiked) {
        await tracksApi.unlike(trackId);
      } else {
        await tracksApi.like(trackId);
      }
      // Invalidate liked tracks query to update LikedTracksPage
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] });
    } catch (error) {
      // Revert on error
      setIsLiked(wasLiked);
      console.error('Failed to toggle like:', error);
    } finally {
      setIsLoading(false);
    }
  }, [trackId, isAuthenticated, isLoading, isLiked, queryClient]);

  if (showOnlyWhenAuthenticated && !isAuthenticated) {
    return null;
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`p-2 rounded-lg transition-all ${
        isLiked
          ? 'text-red-500 hover:text-red-400'
          : 'text-gray-400 hover:text-red-500'
      } ${isLoading ? 'opacity-50' : ''} ${className}`}
      aria-label={isLiked ? 'Unlike' : 'Like'}
    >
      <Heart
        size={size}
        className={isLiked ? 'fill-current' : ''}
      />
    </button>
  );
}
